// Client library - server code imports '@highlight-cp/hooks/universal' instead.

// Debounced callback hooks
export { DEFAULT_DEBOUNCE_DELAY, useDebouncedCallback } from './use-debounced-callback';
export { useLodashDebouncedCallback } from './use-lodash-debounced-callback';

// Endpoint polling
export { useEndpointPolling } from './use-endpoint-polling';

// Project and user data hooks
export { useGetActiveSolutions } from './use-get-active-solutions';
export { useGetAdminOptInSurveys } from './use-get-admin-opt-in-surveys';
export { useGetAudience } from './use-get-audience';
export { useGetBoxCountsPerProduct } from './use-get-box-counts-per-product';
export { useGetCurrentOrgTeams } from './use-get-current-org-teams';
export {
  decorateOrgTeams,
  type DecoratedOrgTeam,
} from './use-get-current-org-teams/utils/decorate-org-teams';
export { useGetCurrentProject } from './use-get-current-project';
export { useGetCurrentProjectId } from './use-get-current-project-id';
export { useGetCurrentUser } from './use-get-current-user';
export { useGetCurrentUserRoles } from './use-get-current-user-roles';
export { useOrgCommunitySlug } from './use-org-community-slug';
export { useGetFieldingGroups } from './use-get-fielding-groups';
export { useDaysToCompleteFielding } from './use-days-to-complete-fielding';
export { getStepDuration } from './utils/get-step-duration';
export { QUESTIONS_PER_RESPONDENT_LIMIT, splitQuestionBudget } from './utils/question-budget';
export { useGetHighlighterProfileSurveys } from './use-get-highlighter-profile-surveys';
export { useGetIsProjectLocked } from './use-get-is-project-locked';
export { isProjectEditable, useIsProjectEditable } from './use-is-project-editable';
export { useGetProducts } from './use-get-products';
export { useGetProjectsList } from './use-get-projects-list';
export { useGetShareLinks } from './use-get-share-links';
export { useGetSurveyConfigs } from './use-get-survey-configs';
export { useGetSurveys } from './use-get-surveys';
export {
  useCustomVariablesFromRecruiting,
  type useCustomVariablesFromRecruitingParams,
} from './use-custom-variables-from-recruiting';
export { useProjectPublicId } from './use-project-public-id';

// API and mutation handling
export { useHandleApiCall, type ApiResult } from './use-handle-api-call';
export { useMutationReducer, MUTATION_ACTIONS } from './use-mutation-reducer';

// Formatting
export { useGetDateValueFormat } from './use-get-date-value-format';
export { useGetNumberFormatters } from './use-get-number-formatters';

// App type detection
export {
  ImpersonationSessionProvider,
  useImpersonationSession,
  type ImpersonationSessionState,
} from './impersonation-session';
export {
  useImpersonation,
  isTargetUserIneligibleImpersonationError,
  TARGET_USER_INELIGIBLE_IMPERSONATION_ERROR,
  type ImpersonationErrorDetails,
} from './use-impersonation';
export { useGetIsImpersonating } from './use-get-is-impersonating';

export {
  CHECKING_IMPERSONATION_ELIGIBILITY_LABEL,
  getEligibleImpersonationCustomers,
  hasSafeCustomerId,
  INELIGIBLE_IMPERSONATION_LABEL,
  INVALID_CUSTOMER_ID_IMPERSONATION_LABEL,
  mapEligibilityByCustomerId,
  parseEligibilityResults,
  useImpersonationEligibility,
  type GetImpersonationEligibility,
  type ImpersonationEligibilityCustomer,
  type ImpersonationEligibilityReason,
  type ImpersonationEligibilityResult,
  type ImpersonationEligibilityState,
  type SafeImpersonationEligibilityCustomer,
} from './use-impersonation-eligibility';
export { useIsAdminApp } from './use-is-admin-app';
export { useProjectType } from './use-project-type';
export { isSequentialMonadicSolution } from './use-project-type';

// UI and interaction hooks
export { useEnterKey, useEscapeKey } from './use-key-press';
export { useRemainingHeight } from './use-remaining-height';
export { useIsSticky } from './use-is-sticky';
export { useScrollFollow } from './use-scroll-follow';
export { useTableKeyboardNavigation, type HotkeyItem } from './use-table-keyboard-navigation';
export { useTypingAnimation } from './use-typing-animation';

// QR code generation
export { useQrCode, generateQrCodeDataUrl } from './use-qr-code';

// entitlements
export { useBYOCStatusByCurrentUser, useBYOCStatusByCurrentProject } from './use-byoc-status';
export { useCostCalculatorStatus } from './use-cost-calculator-status';

// Community context (highlighter-app)
export {
  CommunityContextProvider,
  DEFAULT_COMMUNITY,
  DEFAULT_EU_COMMUNITY,
  getDefaultCommunity,
  useCommunityContext,
  type UseCommunityContextOptions,
} from './community-context';
